class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      searchQuery: "[data-query]",
      searchMessage: "[data-message]",
      input: "[data-search-input]",
      submit: 'button[type="submit"]',
      loading: "[data-spinner]",
      clear: "[data-clear-search]",
      popularSearchItems: ["[data-ps-item]"],
      moreResultIcon: "[data-more-result-icon]",
    };

    this.container = document.querySelector("[data-search-popup]");

    this.domNodes = queryDomNodes(this.selectors, this.container);

    this.cachedResults = {};
    this.transitionDuration = 300;
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.searchPopupSearults = this.querySelector(".m-search-popup__result");
    this.popularSearches = this.querySelector("[data-popular-searches]");
    this.searchCount = this.querySelector("[data-search-count]");

    this.setupEventListeners();
  }

  setPopularSearchesLink = () => {
    const { popularSearchItems } = this.domNodes;
    popularSearchItems.forEach((itm) => (itm.href = createSearchLink(itm.dataset.psQuery)));
  };

  setupEventListeners() {
    const { clear } = this.domNodes;
    const form = this.querySelector("form.m-search-form");
    form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
    clear.addEventListener("click", this.onClearSearch.bind(this));
    MinimogEvents.subscribe(MinimogTheme.pubSubEvents.closeSearchPopup, () => this.close(true));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onClearSearch(event) {
    event.preventDefault();
    this.input.value = "";
    this.onChange();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Hebrew is NOT supported by Shopify's built-in predictive search API: it
    // returns 417 "Unsupported buyer locale", which left the spinner spinning
    // forever. Instead we query the regular full-text /search engine (which does
    // support Hebrew and English) via the Section Rendering API, rendering the
    // existing "page-search" section, then extract the products into the compact
    // dropdown below. A trailing "*" wildcard makes short prefixes match too
    // (e.g. 2-3 letters), so results appear as soon as the shopper starts typing.
    const searchRoot = (window.MinimogSettings.routes && window.MinimogSettings.routes.search_url) || "/search";
    const searchURL = `${searchRoot}?q=${encodeURIComponent(searchTerm + "*")}&type=product&section_id=page-search`;

    fetch(`${searchURL}`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = this.buildResultsMarkup(text);
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  liveSearchStyles() {
    // Scoped styling for the Hebrew live-search dropdown items. Shipped inline so
    // it never depends on where the theme bundles its CSS. Uses logical `gap`
    // (RTL-safe), an 8pt spacing rhythm, subtle row dividers and a hover state.
    return (
      "<style>" +
      ".vnrt-live-search{padding:6px 4px 10px}" +
      ".vnrt-live-search .m-search-result__products-list{display:flex;flex-direction:column;gap:0}" +
      ".vnrt-live-search .m-search-item--with-thumbnail{display:flex;align-items:center;gap:16px;" +
      "padding:14px 14px;text-decoration:none;transition:background-color .15s ease}" +
      ".vnrt-live-search .m-search-item--with-thumbnail+.m-search-item--with-thumbnail{border-top:1px solid rgba(0,0,0,.06)}" +
      ".vnrt-live-search .m-search-item--with-thumbnail:hover{background-color:rgba(0,0,0,.035)}" +
      ".vnrt-live-search .m-search-item--with-thumbnail:focus-visible{background-color:rgba(0,0,0,.05);" +
      "outline:1.5px solid #111;outline-offset:-1.5px}" +
      ".vnrt-live-search .m-search-item--image{flex:0 0 auto;width:58px;height:76px;overflow:hidden;background:#f4f4f4}" +
      ".vnrt-live-search .m-search-item--image img,.vnrt-live-search .m-predictive-search__image{" +
      "width:100%;height:100%;object-fit:cover;display:block;transition:transform .5s cubic-bezier(.2,.6,.2,1)}" +
      ".vnrt-live-search .m-search-item--with-thumbnail:hover .m-search-item--image img{transform:scale(1.06)}" +
      ".vnrt-live-search .m-search-item--content{flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:6px}" +
      ".vnrt-live-search .m-search-item--title{margin:0;font-size:14px;line-height:1.45;font-weight:500;color:#111;" +
      "letter-spacing:.005em;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}" +
      ".vnrt-live-search .m-search-item--price{display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;" +
      "font-size:13.5px;line-height:1.2;font-variant-numeric:tabular-nums}" +
      ".vnrt-live-search .vnrt-price{color:#3a3a3a}" +
      ".vnrt-live-search .vnrt-price--sale{color:#c0182b;font-weight:600}" +
      ".vnrt-live-search .vnrt-price--orig{color:#9a9a9a;font-weight:400;text-decoration:line-through}" +
      "@media (prefers-reduced-motion:reduce){.vnrt-live-search .m-search-item--image img," +
      ".vnrt-live-search .m-search-item--with-thumbnail{transition:none}" +
      ".vnrt-live-search .m-search-item--with-thumbnail:hover .m-search-item--image img{transform:none}}" +
      "</style>"
    );
  }

  buildResultsMarkup(text) {
    const doc = new DOMParser().parseFromString(text, "text/html");
    const cards = Array.from(doc.querySelectorAll(".m-product-card")).slice(0, 8);

    if (!cards.length) {
      return this.liveSearchStyles() + '<div data-search-items-wrapper class="m-search-result vnrt-live-search"></div>';
    }

    const items = cards
      .map((card) => {
        const link = card.querySelector(".m-product-card__name") || card.querySelector("a[href]");
        const url = link ? link.getAttribute("href") : "#";
        const title = link ? link.textContent.trim() : "";
        const imgEl = card.querySelector(".m-product-card__media img, img");
        let img = imgEl ? imgEl.getAttribute("src") || "" : "";
        if (img) img = img.replace(/width=\d+/, "width=150");
        // Build a clean single price rather than reusing the theme's full
        // .m-price block (which renders both regular + sale sub-blocks and relies
        // on theme CSS to hide one). m-price--on-sale is the reliable sale flag.
        const priceBox = card.querySelector(".m-price");
        let priceHTML = "";
        if (priceBox) {
          const txt = (el) => (el ? el.textContent.replace(/\s+/g, " ").trim() : "");
          if (priceBox.classList.contains("m-price--on-sale")) {
            const sale = txt(priceBox.querySelector(".m-price__sale .m-price-item--sale"));
            const orig = txt(priceBox.querySelector(".m-price__sale s"));
            priceHTML =
              `<span class="vnrt-price vnrt-price--sale">${sale}</span>` +
              (orig ? `<s class="vnrt-price vnrt-price--orig">${orig}</s>` : "");
          } else {
            priceHTML =
              `<span class="vnrt-price">${txt(priceBox.querySelector(".m-price__regular .m-price-item--regular"))}</span>`;
          }
        }
        const safeTitle = title.replace(/"/g, "&quot;");
        const imageMarkup = img
          ? `<img class="m-predictive-search__image" src="${img}" alt="${safeTitle}" width="70" loading="lazy">`
          : "";

        return (
          `<a href="${url}" class="m-search-item m-search-item--with-thumbnail">` +
          `<div class="m-search-item--image">${imageMarkup}</div>` +
          `<div class="m-search-item--content">` +
          `<p class="m-search-item--title m:font-medium">${title}</p>` +
          `<div class="m-search-item--price">${priceHTML}</div>` +
          `</div>` +
          `</a>`
        );
      })
      .join("");

    return (
      this.liveSearchStyles() +
      '<div data-search-items-wrapper class="m-search-result vnrt-live-search">' +
      '<div class="m-search-result__group-wrapper">' +
      '<div class="m-search-result__group">' +
      '<div class="m-search-result__products">' +
      '<div class="m-search-result__products-list">' +
      items +
      "</div></div></div></div></div>"
    );
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    const searchItemsWrapper = this.querySelector("[data-search-items-wrapper]");
    if (searchItemsWrapper.childElementCount > 0) {
      this.renderSearchQueryAndMessage(true);
    } else {
      this.renderSearchQueryAndMessage(false);
    }

    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.open();
  }

  renderSearchQueryAndMessage(results) {
    const { input, searchQuery, searchMessage, moreResultIcon } = this.domNodes;
    const query = input.value;

    const { resultsTitle } = searchMessage.dataset;
    searchQuery.textContent = query;

    this.predictiveSearchResults.classList.remove("m:hidden");
    this.searchPopupSearults.classList.remove("m:hidden");

    if (results) {
      searchMessage.textContent = resultsTitle;
      moreResultIcon.classList.add("m:hidden");
    } else {
      searchMessage.textContent = searchMessage.dataset.resultsTitle;
      moreResultIcon.classList.remove("m:hidden");
    }
  }

  toggleSpinnerLoading(show) {
    const { loading, submit } = this.domNodes;
    submit.style.visibility = show ? "hidden" : "visible";
    loading.style.visibility = show ? "visible" : "hidden";
  }

  toggleClearSearch(show) {
    const { clear } = this.domNodes;
    clear.style.visibility = show ? "visible" : "hidden";
  }

  open() {
    this.searchPopupSearults.style.setProperty(
      "--search-result-max-height",
      `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`
    );
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;

    this.popularSearches && this.popularSearches.classList.add("m:hidden");
    this.searchCount.classList.remove("m:hidden");
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
      this.toggleClearSearch(false);
      this.resultsMaxHeight = false;
    }

    this.removeAttribute("open");
    this.predictiveSearchResults.classList.add("m:hidden");
    this.searchPopupSearults.classList.add("m:hidden");
    this.popularSearches && this.popularSearches.classList.remove("m:hidden");
    this.searchCount.classList.add("m:hidden");
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector("predictive-search").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }
}

customElements.define("predictive-search", PredictiveSearch);
